import React, { useState, useCallback, useMemo } from 'react';
import { InboxOutlined, UploadOutlined, DownloadOutlined, CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { Steps, Card, Upload, Button, Table, message, Space, Alert, Progress, Tooltip, Tag } from 'antd';
import Papa from 'papaparse';
import { useTranslation } from 'react-i18next';

const { Dragger } = Upload;

/**
 * Shared Component: Replace SKU Wizard
 *
 * Step 1: Upload CSV
 * Step 2: Preview & Confirm
 *
 * @param {Object} productService - Product Service
 * @param {Function} onSuccess - Callback on success
 * @param {Function} onCancel - Callback on cancel
 * @param {Object} extraParams - Extra parameters for API calls (e.g. merchant_id for operator)
 */
export default function ReplaceSkuWizard({ productService, onSuccess, onCancel, extraParams = {} }) {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [csvFile, setCsvFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [executionResult, setExecutionResult] = useState(null);

  // ========== Step 1: CSV Upload ==========
  const csvUploadProps = {
    name: 'file',
    accept: '.csv',
    fileList: csvFile ? [csvFile] : [],
    beforeUpload: (file) => {
      if (!file.name.endsWith('.csv')) {
        message.error(t('shared.products.replace_sku.errors.invalid_csv'));
        return Upload.LIST_IGNORE;
      }
      setCsvFile(file);
      return false;
    },
    onRemove: () => {
      setCsvFile(null);
    },
    maxCount: 1,
  };

  const handleDownloadTemplate = useCallback(() => {
    const headers = [
      t('shared.products.replace_sku.csv_template.old_sku'),
      t('shared.products.replace_sku.csv_template.new_sku'),
    ];
    const sampleData = [
      ['OLD_SKU_1', 'NEW_SKU_1'],
      ['OLD_SKU_2', 'NEW_SKU_2'],
    ];
    const csvRows = [headers.join(','), ...sampleData.map(row => row.join(','))];
    const csvContent = csvRows.join('\n');
    const blob = new Blob([`\ufeff${csvContent}`], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'replace_sku_template.csv';
    link.click();
  }, [t]);

  // ========== Step 2: Parse & Preview ==========
  const handlePreview = useCallback(async () => {
    if (!csvFile) {
      message.warning(t('shared.products.replace_sku.errors.no_file'));
      return;
    }

    setLoading(true);

    Papa.parse(csvFile, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        const i18nHeaders = {
          oldSku: t('shared.products.replace_sku.csv_template.old_sku'),
          newSku: t('shared.products.replace_sku.csv_template.new_sku'),
        };

        const replacements = results.data.map(row => ({
          old_sku: row[i18nHeaders.oldSku] || row.old_sku || row['Old SKU'] || '',
          new_sku: row[i18nHeaders.newSku] || row.new_sku || row['New SKU'] || '',
        }));

        try {
          const result = await productService.previewReplaceSku({ ...extraParams, replacements });

          setPreviewData(result.data.results);
          setCurrentStep(1);
        } catch (error) {
          console.error('[ReplaceSkuWizard] Preview failed:', error);
          message.error(t('shared.products.replace_sku.errors.preview_failed'));
        } finally {
          setLoading(false);
        }
      },
      error: (error) => {
        message.error(t('shared.products.replace_sku.errors.csv_parse_failed', { error: error.message }));
        setLoading(false);
      },
    });
  }, [csvFile, t, extraParams]);

  // ========== Execute Replacement ==========
  const handleExecute = useCallback(async () => {
    const validItems = previewData.filter(item => item.status === 'valid');
    if (validItems.length === 0) {
      message.warning(t('shared.products.replace_sku.errors.no_valid_data'));
      return;
    }

    setLoading(true);
    try {
      const replacements = validItems.map(item => ({
        old_sku: item.old_sku,
        new_sku: item.new_sku,
      }));

      const result = await productService.replaceSku({ ...extraParams, replacements });
      setExecutionResult(result.data);

      // Update preview data with execution results
      // The backend returns the full list with updated statuses
      if (result.data.results) {
        setPreviewData(result.data.results);
      }

      message.success(t('shared.products.replace_sku.success.complete', {
        success: result.data.success_count,
        failed: result.data.failed_count
      }));

      if (onSuccess) onSuccess(result.data);

    } catch (error) {
      console.error('[ReplaceSkuWizard] Execution failed:', error);
      message.error(t('shared.products.replace_sku.errors.execution_failed'));
    } finally {
      setLoading(false);
    }
  }, [previewData, extraParams, t, onSuccess]);

  // ========== Columns ==========
  const columns = useMemo(() => [
    {
      title: t('shared.products.replace_sku.preview.old_sku'),
      dataIndex: 'old_sku',
      key: 'old_sku',
    },
    {
      title: t('shared.products.replace_sku.preview.new_sku'),
      dataIndex: 'new_sku',
      key: 'new_sku',
    },
    {
      title: t('shared.products.replace_sku.preview.status'),
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => {
        if (status === 'valid') {
          return <Tag color="success">{t('shared.products.replace_sku.preview.valid')}</Tag>;
        }
        if (status === 'success') {
          return <Tag color="success">{t('shared.products.replace_sku.status.success')}</Tag>;
        }
        return (
          <Tooltip title={record.message ? t(`shared.products.replace_sku.errors.${record.message}`, { defaultValue: record.message }) : undefined}>
            <Tag color="error">{t('shared.products.replace_sku.preview.invalid')}</Tag>
          </Tooltip>
        );
      },
    },
    {
      title: t('shared.products.replace_sku.preview.message'),
      dataIndex: 'message',
      key: 'message',
      render: (text, record) => {
        if (record.status === 'valid' || record.status === 'success') return '-';
        if (!text) return '-';
        return <span className="text-danger">{t(`shared.products.replace_sku.errors.${text}`, { defaultValue: text })}</span>;
      },
    },
  ], [t]);

  const steps = [
    {
      title: t('shared.products.replace_sku.steps.upload_csv'),
      content: (
        <div style={{ padding: '24px 0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
            <span>{t('shared.products.replace_sku.step1.description')}</span>
            <Button icon={<DownloadOutlined />} onClick={handleDownloadTemplate}>
              {t('shared.products.replace_sku.step1.download_template')}
            </Button>
          </div>

          <Dragger {...csvUploadProps} style={{ padding: '20px' }}>
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">{t('shared.products.replace_sku.step1.drag_text')}</p>
          </Dragger>
        </div>
      ),
    },
    {
      title: t('shared.products.replace_sku.steps.preview'),
      content: (
        <div style={{ padding: '24px 0' }}>
          {executionResult && (
            <Alert
              message={t('shared.products.replace_sku.step2.result_summary', {
                success: executionResult.success_count,
                failed: executionResult.failed_count
              })}
              type={executionResult.failed_count > 0 ? 'warning' : 'success'}
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}
          <Table
            columns={columns}
            dataSource={previewData}
            rowKey={(record) => `${record.old_sku}-${record.new_sku}`}
            pagination={false}
            size="small"
            scroll={{ y: 400 }}
          />
        </div>
      ),
    },
  ];

  return (
    <Card>
      <Steps current={currentStep} items={steps.map(s => ({ title: s.title }))} style={{ marginBottom: 24 }} />
      <div>{steps[currentStep].content}</div>
      <div style={{ marginTop: 24, textAlign: 'right' }}>
        <Space>
          {currentStep === 0 && (
            <Button type="primary" onClick={handlePreview} loading={loading}>
              {t('common.button.next')}
            </Button>
          )}
          {currentStep === 1 && !executionResult && (
            <>
              <Button onClick={() => setCurrentStep(0)}>{t('common.button.previous')}</Button>
              <Button type="primary" onClick={handleExecute} loading={loading}>
                {t('common.button.confirm')}
              </Button>
            </>
          )}
          {executionResult && (
            <Button type="primary" onClick={onCancel}>
              {t('common.button.close')}
            </Button>
          )}
        </Space>
      </div>
    </Card>
  );
}
